import { useState } from 'react';
import { toast } from 'sonner';

import {
  User,
  Mail,
  Smartphone,
  Lock,
  Camera,
  Save,
  Loader2,
  Eye,
  EyeOff,
} from 'lucide-react';

import { api } from '../services/client';
import { useTranslation } from '../i18n';
import { getImageUrl } from '../utils/imageUrl';
import {
  COUNTRY_CODES,
  DEFAULT_COUNTRY_CODE,
  validatePhoneByCountry,
  getPhonePlaceholder,
} from '../constants/countryCodes';
import { Input } from './ui/input';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Card, CardContent } from './ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import ImagePreviewOverlay from './ImagePreviewOverlay';

interface IMyAccountProps {
  currentUser: any;
  onUserUpdated: (user: any) => void;
}

const MyAccount = ({ currentUser, onUserUpdated }: IMyAccountProps) => {
  const { t } = useTranslation();
  const [displayName, setDisplayName] = useState(
    currentUser?.displayName || '',
  );
  const [email, setEmail] = useState(currentUser?.email || '');
  const [countryCode, setCountryCode] = useState(
    currentUser?.countryCode || DEFAULT_COUNTRY_CODE,
  );
  const [mobileNumber, setMobileNumber] = useState(
    currentUser?.mobileNumber || '',
  );
  const [saving, setSaving] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Change password
  const [showPwdModal, setShowPwdModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdError, setPwdError] = useState('');
  const [showCurrentPwd, setShowCurrentPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      toast.error(t('account.avatar_invalid_format'));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('account.avatar_too_large'));
      return;
    }

    setAvatarUploading(true);
    try {
      const result = await api.upload<{ imageUrl: string; admin: any }>(
        '/backoffice/auth/upload-avatar',
        file,
      );

      // Append cache-busting param to force browser to reload the new avatar
      const admin = result.admin;

      if (admin?.avatar && !admin.avatar.startsWith('data:')) {
        admin.avatar = admin.avatar.includes('?')
          ? `${admin.avatar}&t=${Date.now()}`
          : `${admin.avatar}?t=${Date.now()}`;
      }

      onUserUpdated(admin);
      toast.success(t('account.avatar_updated'));
    } catch (err: any) {
      toast.error(err.message || t('common.operation_failed'));
    } finally {
      setAvatarUploading(false);
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!displayName.trim()) {
      newErrors.displayName = t('account.error_name_required');
    } else if (displayName.trim().length < 3) {
      newErrors.displayName = t('account.error_name_min');
    } else if (displayName.includes('*')) {
      newErrors.displayName = t('validation.contains_masked_content');
    }

    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = t('account.error_email_invalid');
    }

    if (mobileNumber && !validatePhoneByCountry(countryCode, mobileNumber)) {
      newErrors.mobile = t('validation.mobile_invalid_for_country');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveProfile = async () => {
    if (!validate()) return;

    setSaving(true);
    try {
      const updated = await api.patch<any>('/backoffice/auth/profile', {
        displayName: displayName.trim(),
        email: email.trim(),
        mobileNumber: mobileNumber.trim(),
        countryCode: mobileNumber.trim() ? countryCode : '',
      });
      onUserUpdated(updated);
      setErrors({});
      toast.success(t('common.save_success'));
    } catch (err: any) {
      toast.error(err.message || t('common.save_failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    setPwdError('');

    if (!currentPassword) {
      setPwdError(t('account.error_current_password_required'));
      return;
    }

    if (newPassword.length < 9) {
      setPwdError(t('account.password_min_length'));
      return;
    }

    if (!/[a-z]/.test(newPassword)) {
      setPwdError(t('account.password_needs_lowercase'));
      return;
    }

    if (!/[A-Z]/.test(newPassword)) {
      setPwdError(t('account.password_needs_uppercase'));
      return;
    }

    if (!/\d/.test(newPassword)) {
      setPwdError(t('account.password_needs_number'));
      return;
    }

    if (!/[@$!%*?&]/.test(newPassword)) {
      setPwdError(t('account.password_needs_special'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setPwdError(t('account.passwords_mismatch'));
      return;
    }

    setPwdSaving(true);
    try {
      // The change revokes every earlier token of this admin, this one
      // included; keep the replacement so this browser stays signed in.
      const res = await api.post<{ token?: string }>(
        '/backoffice/auth/change-password',
        { currentPassword, newPassword },
      );
      if (res?.token) localStorage.setItem('adminToken', res.token);
      setShowPwdModal(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success(t('common.save_success'));
    } catch (err: any) {
      setPwdError(err.message || t('common.operation_failed'));
    } finally {
      setPwdSaving(false);
    }
  };

  return (
    <div className="p-4 lg:p-8 flex flex-col gap-6 max-w-3xl">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">
          {t('account.title')}
        </h1>
        <p className="text-base text-gray-600 mt-2">{t('account.subtitle')}</p>
      </div>

      {/* Avatar */}
      <Card>
        <CardContent className="pt-6">
          <h3 className="text-base font-semibold text-foreground mb-4">
            {t('account.avatar')}
          </h3>
          <div className="flex items-center gap-5">
            <div className="relative">
              {currentUser?.avatar ? (
                <button
                  type="button"
                  className="rounded-full"
                  aria-label={t('common.view_avatar')}
                  onClick={() =>
                    setPreviewImage(getImageUrl(currentUser.avatar)!)
                  }
                >
                  <img
                    src={getImageUrl(currentUser.avatar)}
                    alt="avatar"
                    className="w-20 h-20 rounded-full object-cover cursor-pointer"
                  />
                </button>
              ) : (
                <div className="w-20 h-20 rounded-full bg-blue-500 flex items-center justify-center text-white text-3xl font-bold">
                  {(currentUser?.displayName || currentUser?.username || 'A')
                    .charAt(0)
                    .toUpperCase()}
                </div>
              )}
              <label className="absolute bottom-0 right-0 w-7 h-7 bg-white rounded-full border border-gray-200 shadow-sm flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors">
                {avatarUploading ? (
                  <Loader2 className="w-4 h-4 text-gray-500 animate-spin" />
                ) : (
                  <Camera className="w-4 h-4 text-gray-500" />
                )}
                <input
                  type="file"
                  accept="image/jpeg,image/png"
                  onChange={handleAvatarUpload}
                  className="hidden"
                  disabled={avatarUploading}
                />
              </label>
            </div>
            <div>
              <p className="text-sm text-[#364153]">
                {t('account.avatar_hint')}
              </p>
              <p className="text-xs text-[#6A7282] mt-0.5">
                {t('account.avatar_formats')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Basic Info */}
      <Card>
        <CardContent className="pt-6">
          <h3 className="text-base font-semibold text-foreground mb-5">
            {t('account.basic_info')}
          </h3>

          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="flex items-center gap-1.5 text-sm font-medium text-[#364153] mb-2">
                <User className="w-4 h-4" />
                {t('account.display_name')}
              </label>
              <Input
                type="text"
                value={displayName}
                onChange={(e) => {
                  setDisplayName(e.target.value);

                  if (errors.displayName)
                    setErrors((prev) => ({ ...prev, displayName: '' }));
                }}
                className={errors.displayName ? 'border-red-300' : ''}
              />
              {errors.displayName && (
                <p className="text-xs text-red-500 mt-1">
                  {errors.displayName}
                </p>
              )}
            </div>
            <div>
              <label className="flex items-center gap-1.5 text-sm font-medium text-[#364153] mb-2">
                <User className="w-4 h-4" />
                {t('account.username')}
              </label>
              <Input type="text" value={currentUser?.username || ''} disabled />
            </div>
          </div>

          {/* Email */}
          <div className="mt-5">
            <label className="flex items-center gap-1.5 text-sm font-medium text-[#364153] mb-2">
              <Mail className="w-4 h-4" />
              {t('account.email')}
            </label>
            <Input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);

                if (errors.email) setErrors((prev) => ({ ...prev, email: '' }));
              }}
              placeholder="admin@example.com"
              className={errors.email ? 'border-red-300' : ''}
            />
            {errors.email && (
              <p className="text-xs text-red-500 mt-1">{errors.email}</p>
            )}
          </div>

          {/* Phone */}
          <div className="mt-5">
            <label className="flex items-center gap-1.5 text-sm font-medium text-[#364153] mb-2">
              <Smartphone className="w-4 h-4" />
              {t('account.mobile')}
            </label>
            <div className="flex gap-2">
              <Select
                value={countryCode}
                onValueChange={(val) => {
                  setCountryCode(val);

                  if (errors.mobile)
                    setErrors((prev) => ({ ...prev, mobile: '' }));
                }}
              >
                <SelectTrigger className="w-auto min-w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRY_CODES.map(({ code, label }) => (
                    <SelectItem key={code} value={code}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="text"
                value={mobileNumber}
                onChange={(e) => {
                  setMobileNumber(e.target.value);

                  if (errors.mobile)
                    setErrors((prev) => ({ ...prev, mobile: '' }));
                }}
                placeholder={getPhonePlaceholder(countryCode)}
                className={`flex-1 ${errors.mobile ? 'border-red-300' : ''}`}
              />
            </div>
            {errors.mobile && (
              <p className="text-xs text-red-500 mt-1">{errors.mobile}</p>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button
              variant="outline"
              onClick={() => {
                setDisplayName(currentUser?.displayName || '');
                setEmail(currentUser?.email || '');
                setCountryCode(currentUser?.countryCode || '60');
                setMobileNumber(currentUser?.mobileNumber || '');
              }}
              disabled={
                displayName === (currentUser?.displayName || '') &&
                email === (currentUser?.email || '') &&
                countryCode === (currentUser?.countryCode || '60') &&
                mobileNumber === (currentUser?.mobileNumber || '')
              }
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="default"
              onClick={handleSaveProfile}
              disabled={
                saving ||
                (displayName === (currentUser?.displayName || '') &&
                  email === (currentUser?.email || '') &&
                  countryCode === (currentUser?.countryCode || '60') &&
                  mobileNumber === (currentUser?.mobileNumber || ''))
              }
            >
              <Save className="w-4 h-4" />
              {saving ? t('common.saving') : t('common.save_changes')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Security */}
      <Card>
        <CardContent className="pt-6">
          <h3 className="text-base font-semibold text-foreground mb-5">
            {t('account.security')}
          </h3>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Lock className="w-5 h-5 text-[#6A7282]" />
              <div>
                <div className="text-sm font-semibold text-foreground">
                  {t('account.login_password')}
                </div>
                <div className="text-sm text-muted-foreground mt-0.5">
                  {t('account.password_hint')}
                </div>
              </div>
            </div>
            <Button variant="default" onClick={() => setShowPwdModal(true)}>
              {t('account.change_password')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Change Password Modal */}
      <Dialog open={showPwdModal} onOpenChange={setShowPwdModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('account.change_password')}</DialogTitle>
            <DialogDescription>
              {t('account.change_password_desc')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1.5">
                {t('account.current_password')}
              </label>
              <div className="relative">
                <Input
                  type={showCurrentPwd ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder={t('account.current_password_placeholder')}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => setShowCurrentPwd(!showCurrentPwd)}
                  tabIndex={-1}
                >
                  {showCurrentPwd ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1.5">
                {t('account.new_password')}
              </label>
              <div className="relative">
                <Input
                  type={showNewPwd ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={t('account.new_password_placeholder')}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => setShowNewPwd(!showNewPwd)}
                  tabIndex={-1}
                >
                  {showNewPwd ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {t('account.password_policy_hint')}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1.5">
                {t('account.confirm_password')}
              </label>
              <div className="relative">
                <Input
                  type={showConfirmPwd ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t('account.confirm_password_placeholder')}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => setShowConfirmPwd(!showConfirmPwd)}
                  tabIndex={-1}
                >
                  {showConfirmPwd ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            {pwdError && (
              <div className="bg-red-50 text-red-600 px-4 py-2.5 rounded-lg text-sm">
                {pwdError}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowPwdModal(false);
                setPwdError('');
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
                setShowCurrentPwd(false);
                setShowNewPwd(false);
                setShowConfirmPwd(false);
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="default"
              onClick={handleChangePassword}
              disabled={
                pwdSaving ||
                !currentPassword ||
                !newPassword ||
                !confirmPassword
              }
            >
              {pwdSaving ? t('common.saving') : t('common.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Preview Overlay */}
      {previewImage && (
        <ImagePreviewOverlay
          src={previewImage}
          alt={currentUser?.displayName || ''}
          onClose={() => setPreviewImage(null)}
        />
      )}
    </div>
  );
};

export default MyAccount;
